import { agentBrand, defaultAgentBrand, type AgentBrand } from "./brand.js";
import type { ApiErrorBody, OperationView } from "./types.js";
import { reportAgentApiFailure } from "./error-reporting.js";

type ApiReachabilityListener = (reachable: boolean) => void;

const apiReachabilityListeners = new Set<ApiReachabilityListener>();

export class AgentApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryable = false,
    /** The server's request ID, for support and error reports. */
    readonly requestId: string | null = null,
  ) {
    super(message);
    this.name = "AgentApiError";
  }
}

export async function api<T>(
  path: string,
  input: Omit<RequestInit, "credentials" | "cache"> = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...input,
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        accept: "application/json",
        ...(input.body === undefined
          ? {}
          : { "content-type": "application/json" }),
        ...input.headers,
      },
    });
  } catch (error) {
    // Changing conversations cancels stale reads. That cancellation says
    // nothing about connectivity and must not disable the current composer.
    if (input.signal?.aborted) throw error;
    reportApiReachability(false);
    throw new AgentApiError(
      0,
      "network_unavailable",
      networkUnavailableMessage(),
      true,
    );
  }
  reportApiReachability(true);
  const text = await response.text();
  let value: unknown = null;
  if (text) {
    try {
      value = JSON.parse(text);
    } catch {
      value = null;
    }
  }
  if (!response.ok) {
    if (response.status === 401 && typeof window !== "undefined") {
      // Re-enter through the server's OpenCloud browser-session check.
      window.location.reload();
    }
    const body = record(value) as ApiErrorBody | null;
    const requestId = response.headers.get("x-request-id");
    reportAgentApiFailure({
      status: response.status,
      code: body?.code ?? "request_failed",
      requestId,
      method: input.method ?? "GET",
      path: path.split("?", 1)[0] ?? path,
    });
    throw new AgentApiError(
      response.status,
      body?.code ?? "request_failed",
      body?.message ?? body?.error ?? friendlyError(response.status),
      body?.retryable ?? response.status >= 500,
      requestId,
    );
  }
  return value as T;
}

/** The offline message, naming the installation's product at call time. */
export function networkUnavailableMessage(
  brand: AgentBrand = agentBrand(),
): string {
  return `${brand.productName} could not be reached. Try again when you're back online.`;
}

/**
 * The default brand's offline wording, kept for existing imports. Runtime
 * code calls `networkUnavailableMessage()` so the active brand is named.
 */
export const NETWORK_UNAVAILABLE_MESSAGE =
  networkUnavailableMessage(defaultAgentBrand);

export function subscribeApiReachability(
  listener: ApiReachabilityListener,
): () => void {
  apiReachabilityListeners.add(listener);
  return () => {
    apiReachabilityListeners.delete(listener);
  };
}

function reportApiReachability(reachable: boolean): void {
  for (const listener of apiReachabilityListeners) listener(reachable);
}

export function requestBody<T extends Record<string, unknown>>(value?: T): string {
  return JSON.stringify({
    requestId: browserRequestId(),
    sentAt: new Date().toISOString(),
    ...(value ?? {}),
  });
}

export function browserRequestId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function followOperation(
  operation: OperationView,
  maximumPolls = 90,
): Promise<OperationView> {
  let current = operation;
  for (let poll = 0; poll < maximumPolls; poll += 1) {
    if (["succeeded", "failed", "cancelled"].includes(current.state)) break;
    await new Promise((resolve) => window.setTimeout(resolve, 700));
    current = await api<OperationView>(`/v1/operations/${encodeURIComponent(current.id)}`);
  }
  if (current.state !== "succeeded") {
    throw new AgentApiError(
      409,
      `operation_${current.state}`,
      current.state === "running" || current.state === "queued"
        ? "This is taking longer than expected. It is safe to return later."
        : `The operation ${current.state}.`,
      current.state === "running" || current.state === "queued",
    );
  }
  return current;
}

function friendlyError(status: number): string {
  if (status === 401) return "Sign in to continue.";
  if (status === 403) return "You do not have access to do that.";
  if (status === 404) return "That item is no longer available.";
  if (status === 409) return "The app changed before that action could finish.";
  if (status >= 500)
    return `${agentBrand().productName} is temporarily unavailable. Please try again.`;
  return "The request could not be completed.";
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
